import type { FormlyFieldConfig } from '@ngx-formly/core';
import type Highcharts from 'highcharts';
import type {
	ChartCardConfig,
	NumberPresentationOptions,
} from '../../../models/chart-config.model';
import type { NumberChartFormModel } from './jepto-number-chart-form.model';
import { FORMAT_PRECISION_OPTIONS } from '../../../utils/table-grid-column.util';
import {
	FORMLY_JEPTO_SELECT,
	FORMLY_MAT_OUTLINE,
} from '../editor-formly-defaults';
import { NUMBER_DEFAULT_PRECISION } from './jepto-number-chart-format.util';

/** Number cards show the sparkline unless the card explicitly turns it off. */
export const NUMBER_DEFAULT_SHOW_SPARKLINE = true;

const LINE_WIDTH_OPTIONS = [1, 2, 3, 4, 5, 6].map(n => ({
	label: String(n),
	value: n,
}));

export function getNumberChartFormFields(): FormlyFieldConfig[] {
	return [
		{
			key: 'valueSize',
			...FORMLY_JEPTO_SELECT,
			defaultValue: 'lg',
			props: {
				...FORMLY_MAT_OUTLINE,
				label: 'Value size',
				options: [
					{ value: 'sm', label: 'Small' },
					{ value: 'md', label: 'Medium' },
					{ value: 'lg', label: 'Large' },
					{ value: 'xl', label: 'Extra Large' },
				],
			},
		},
		{
			key: 'numberFormat',
			wrappers: ['expansion-panel'],
			props: { label: 'Number Format', expansion_expanded: true },
			fieldGroup: [
				{
					key: 'prefix',
					type: 'input',
					defaultValue: '',
					props: {
						...FORMLY_MAT_OUTLINE,
						label: 'Prefix',
						placeholder: '$',
					},
				},
				{
					key: 'suffix',
					type: 'input',
					defaultValue: '',
					props: {
						...FORMLY_MAT_OUTLINE,
						label: 'Suffix',
						placeholder: '%',
					},
				},
				{
					key: 'precision',
					...FORMLY_JEPTO_SELECT,
					defaultValue: NUMBER_DEFAULT_PRECISION,
					props: {
						...FORMLY_MAT_OUTLINE,
						label: 'Precision',
						options: FORMAT_PRECISION_OPTIONS,
					},
				},
				{
					key: 'compact',
					type: 'toggle',
					defaultValue: false,
					className: 'chart-options-toggle-row',
					props: { label: 'Compact notation (K/M/B)' },
				},
			],
		},
		{
			key: 'sparkline',
			wrappers: ['expansion-panel'],
			props: { label: 'Sparkline', expansion_expanded: true },
			fieldGroup: [
				{
					key: 'show',
					type: 'toggle',
					defaultValue: NUMBER_DEFAULT_SHOW_SPARKLINE,
					className: 'chart-options-toggle-row',
					props: { label: 'Show sparkline' },
				},
				{
					key: 'lineWidth',
					...FORMLY_JEPTO_SELECT,
					defaultValue: 2,
					props: {
						...FORMLY_MAT_OUTLINE,
						label: 'Line width',
						options: LINE_WIDTH_OPTIONS,
					},
				},
			],
		},
	];
}

export function resolveNumberSparklineLineWidth(
	presentation?: NumberPresentationOptions,
	visualization?: Highcharts.Options
): number {
	if (typeof presentation?.lineWidth === 'number') {
		return presentation.lineWidth;
	}
	const fromViz = (
		visualization?.plotOptions?.series as { lineWidth?: number } | undefined
	)?.lineWidth;
	return typeof fromViz === 'number' ? fromViz : 2;
}

export function numberFormModelToHighcharts(
	model: NumberChartFormModel,
	currentOptions?: Highcharts.Options
): Partial<Highcharts.Options> {
	return {
		...(currentOptions ?? {}),
		tooltip: {
			...(currentOptions?.tooltip as object),
			enabled: false,
		},
		legend: {
			...(currentOptions?.legend as object),
			enabled: false,
		},
	};
}

export function highchartsToNumberFormModel(
	options: Highcharts.Options,
	cardConfig: ChartCardConfig | null
): NumberChartFormModel {
	const numberOptions = cardConfig?.presentation?.number;
	const lineWidth = resolveNumberSparklineLineWidth(numberOptions, options);
	const numberFormat = numberOptions?.format;

	return {
		valueSize: numberOptions?.valueSize ?? 'lg',
		numberFormat: {
			prefix: numberFormat?.prefix ?? '',
			suffix: numberFormat?.suffix ?? '',
			precision: numberFormat?.precision ?? NUMBER_DEFAULT_PRECISION,
			compact: numberFormat?.compact ?? false,
		},
		sparkline: {
			show: numberOptions?.showSparkline ?? NUMBER_DEFAULT_SHOW_SPARKLINE,
			lineWidth,
		},
	};
}

export function numberFormModelToPresentation(
	model: NumberChartFormModel,
	current?: NumberPresentationOptions
): NumberPresentationOptions {
	return {
		...(current ?? {}),
		valueSize: model.valueSize ?? current?.valueSize ?? 'lg',
		format: {
			...(current?.format ?? {}),
			prefix: model.numberFormat?.prefix ?? current?.format?.prefix ?? '',
			suffix: model.numberFormat?.suffix ?? current?.format?.suffix ?? '',
			precision:
				model.numberFormat?.precision ??
				current?.format?.precision ??
				NUMBER_DEFAULT_PRECISION,
			compact:
				model.numberFormat?.compact ??
				current?.format?.compact ??
				false,
		},
		showSparkline:
			model.sparkline?.show ??
			current?.showSparkline ??
			NUMBER_DEFAULT_SHOW_SPARKLINE,
		lineWidth: model.sparkline?.lineWidth ?? current?.lineWidth ?? 2,
	};
}
